import {
    Dialog,
    DialogContent,
    DialogTitle,
    Typography,
    ListItem,
    ListItemButton,
    ListItemText,
    List,
    Divider,
    Avatar,
    Box,
    IconButton,
    Chip,
    ListItemIcon,
    Fade,
} from "@mui/material"
import {
    Close as CloseIcon,
    Logout as LogoutIcon,
    Settings as SettingsIcon,
    Business as BusinessIcon,
    Email as EmailIcon,
    Person as PersonIcon,
} from "@mui/icons-material"
import {useTranslation} from "react-i18next";

const UserDialog = ({
                        open,
                        onClose,
                        userEmail,
                        userName,
                        userRole,
                        tenantName,
                        avatarUrl,
                        onManageAccount,
                        onSettings,
                        onSwitchTenant,
                        onLogout,
                        isOnline = true,
                    }) => {

    const {t} = useTranslation()
    // Get user initials for avatar fallback
    const getUserInitials = (name, email) => {
        if (name) {
            return name
                .split(" ")
                .map((n) => n[0])
                .join("")
                .toUpperCase()
                .slice(0, 2)
        }
        if (email) {
            return email.charAt(0).toUpperCase()
        }
        return "U"
    }

    const handleClose = (event) => {
        event?.stopPropagation()
        onClose()
    }

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            TransitionComponent={Fade}
            TransitionProps={{timeout: 200}}
            PaperProps={{
                sx: {
                    position: "absolute",
                    top: "60px",
                    right: "16px",
                    margin: 0,
                    width: 360,
                    maxWidth: "90vw",
                    bgcolor: "background.paper",
                    borderRadius: 2,
                    boxShadow: (theme) => theme.shadows[8],
                    overflow: "visible",
                    "&::before": {
                        content: '""',
                        position: "absolute",
                        top: -8,
                        right: 20,
                        width: 16,
                        height: 16,
                        bgcolor: "background.paper",
                        transform: "rotate(45deg)",
                        borderLeft: 1,
                        borderTop: 1,
                        borderColor: "divider",
                    },
                },
            }}
            BackdropProps={{
                sx: {backgroundColor: "transparent"},
            }}
        >
            <DialogTitle
                sx={{
                    pb: 1,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                }}
            >
                <Box sx={{display: "flex", alignItems: "center", gap: 1}}>
                    <BusinessIcon color="primary" fontSize="small"/>
                    <Typography variant="h6" component="span" noWrap>
                        {tenantName}
                    </Typography>
                </Box>
                <IconButton size="small" onClick={handleClose} sx={{ml: 1}}>
                    <CloseIcon fontSize="small"/>
                </IconButton>
            </DialogTitle>

            <Divider/>

            <DialogContent sx={{p: 0}}>
                {/* User Info Section */}
                <Box sx={{p: 2, bgcolor: "grey.50"}}>
                    <Box sx={{display: "flex", alignItems: "center", gap: 2, mb: 1}}>
                        <Box sx={{position: "relative"}}>
                            <Avatar
                                src={avatarUrl}
                                sx={{
                                    width: 48,
                                    height: 48,
                                    bgcolor: "primary.main",
                                    fontSize: "1.2rem",
                                    fontWeight: "bold",
                                }}
                            >
                                {getUserInitials(userName, userEmail)}
                            </Avatar>
                            {/* Online status indicator */}
                            <Box
                                sx={{
                                    position: "absolute",
                                    bottom: 2,
                                    right: 2,
                                    width: 12,
                                    height: 12,
                                    borderRadius: "50%",
                                    bgcolor: isOnline ? "success.main" : "grey.400",
                                    border: 2,
                                    borderColor: "background.paper",
                                }}
                            />
                        </Box>
                        <Box sx={{flex: 1, minWidth: 0}}>
                            {userName && (
                                <Typography variant="subtitle1" fontWeight="medium" noWrap>
                                    {userName}
                                </Typography>
                            )}
                            <Box sx={{display: "flex", alignItems: "center", gap: 0.5, mb: 0.5}}>
                                <EmailIcon fontSize="small" color="action"/>
                                <Typography variant="body2" color="text.secondary" noWrap>
                                    {userEmail}
                                </Typography>
                            </Box>
                            {userRole && (
                                <Chip
                                    label={userRole}
                                    size="small"
                                    variant="outlined"
                                    color="primary"
                                    sx={{height: 20, fontSize: "0.7rem"}}
                                />
                            )}
                        </Box>
                    </Box>
                </Box>

                {/* Menu Items */}
                <List sx={{py: 1}}>
                    <ListItem disablePadding>
                        <ListItemButton
                            onClick={onManageAccount}
                            sx={{
                                py: 1.5,
                                "&:hover": {
                                    bgcolor: "primary.light",
                                    "& .MuiListItemIcon-root": {
                                        color: "primary.main",
                                    },
                                },
                            }}
                        >
                            <ListItemIcon sx={{minWidth: 40}}>
                                <PersonIcon fontSize="small"/>
                            </ListItemIcon>
                            <ListItemText
                                primary={t("ManageUserAccount") || "Manage Account"}
                                secondary={t("ProfileAndPreferences") || "Profile & preferences"}
                                secondaryTypographyProps={{
                                    variant: "caption",
                                    color: "text.secondary",
                                }}
                            />
                        </ListItemButton>
                    </ListItem>

                    {onSettings && (
                        <ListItem disablePadding>
                            <ListItemButton
                                onClick={onSettings}
                                sx={{
                                    py: 1.5,
                                    "&:hover": {
                                        bgcolor: "primary.light",
                                        "& .MuiListItemIcon-root": {
                                            color: "primary.main",
                                        },
                                    },
                                }}
                            >
                                <ListItemIcon sx={{minWidth: 40}}>
                                    <SettingsIcon fontSize="small"/>
                                </ListItemIcon>
                                <ListItemText
                                    primary={t("Settings") || "Settings"}
                                    secondary={t("AppPreferences") || "App preferences"}
                                    secondaryTypographyProps={{
                                        variant: "caption",
                                        color: "text.secondary",
                                    }}
                                />
                            </ListItemButton>
                        </ListItem>
                    )}

                    {onSwitchTenant && (
                        <ListItem disablePadding>
                            <ListItemButton
                                onClick={onSwitchTenant}
                                sx={{
                                    py: 1.5,
                                    "&:hover": {
                                        bgcolor: "primary.light",
                                        "& .MuiListItemIcon-root": {
                                            color: "primary.main",
                                        },
                                    },
                                }}
                            >
                                <ListItemIcon sx={{minWidth: 40}}>
                                    <BusinessIcon fontSize="small"/>
                                </ListItemIcon>
                                <ListItemText
                                    primary={t("SwitchOrganization") || "Switch Organization"}
                                    secondary={t("ChangeWorkspace") || "Change workspace"}
                                    secondaryTypographyProps={{
                                        variant: "caption",
                                        color: "text.secondary",
                                    }}
                                />
                            </ListItemButton>
                        </ListItem>
                    )}

                    <Divider sx={{my: 1}}/>

                    <ListItem disablePadding>
                        <ListItemButton
                            onClick={onLogout}
                            sx={{
                                py: 1.5,
                                "&:hover": {
                                    bgcolor: "error.light",
                                    "& .MuiListItemIcon-root": {
                                        color: "error.main",
                                    },
                                    "& .MuiListItemText-primary": {
                                        color: "error.main",
                                    },
                                },
                            }}
                        >
                            <ListItemIcon sx={{minWidth: 40}}>
                                <LogoutIcon fontSize="small"/>
                            </ListItemIcon>
                            <ListItemText
                                primary={t("SignOut") || "Sign Out"}
                                secondary={t("EndCurrentSession") || "End current session"}
                                secondaryTypographyProps={{
                                    variant: "caption",
                                    color: "text.secondary",
                                }}
                            />
                        </ListItemButton>
                    </ListItem>
                </List>
            </DialogContent>
        </Dialog>
    )
}

export default UserDialog
